/// <amd-module name="CodeCoverage/AllTab/CodeCoverageTree" />
import * as React from "react";

import { ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { VssComponent, IVssComponentContext, css } from "VSS/Platform/Layout";
import { Icon, IconSize } from "VSSUI/Icon";
import { ListSelection, IListSelection } from "VSSUI/List";
import { ITableColumn, ITableBreakpoint, SimpleTableCell, TableLoadingRow, ColumnJustification } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, ITreeRowDetails, Tree } from "VSSUI/TreeEx";
import { ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

import { ICodeCoverageTreeItem } from "CodeCoverage/AllTab/Contracts";
import { getFileName } from "CodeCoverage/AllTab/Helpers";
import * as Resources from "CodeCoverage/AllTab/Resources";
import { CoveredBarChart } from "CodeCoverage/ViewCommon/Components/CoveredBarChart";

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [-100, 140, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [-100, 140, 100, 100]
    },
    {
        breakpoint: ScreenBreakpoints.medium,
        columnWidths: [-100, 140, 140, 140]
    }
];

export interface ICodeCoverageTreeProps {
    itemProvider: TreeItemProvider<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>;
    onToggle: (treeItem: ITreeItemEx<ICodeCoverageTreeItem>) => void;
    onFileRowClick?: (event: React.SyntheticEvent<HTMLElement>, treeItem: ICodeCoverageTreeItem) => void;
}

export class CodeCoverageTree extends VssComponent<ICodeCoverageTreeProps> {
    private selection: IListSelection;
    private columns: ITreeColumn<ICodeCoverageTreeItem>[];

    constructor(props: ICodeCoverageTreeProps, context: IVssComponentContext) {
        super(props, context);

        this.selection = new ListSelection({ multiSelect: false, selectOnFocus: false });
        this.columns = getTreeColumns();
    }

    public render(): JSX.Element {
        const { itemProvider } = this.props;

        return (
            <Tree<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>
                columns={this.columns}
                tableBreakpoints={tableBreakpoints}
                itemProvider={itemProvider}
                onActivate={(event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<ICodeCoverageTreeItem>) => {
                    if (!event.defaultPrevented) {
                        if (!treeRow.data.underlyingItem.data.isDirectory) {
                            if (!ObservableLike.isObservable(treeRow.data.underlyingItem.data) && this.props.onFileRowClick) {
                                this.props.onFileRowClick(event, treeRow.data.underlyingItem.data);
                            }
                        } else {
                            this.onToggle(event, treeRow.data);
                        }
                    }
                }}
                onToggle={this.onToggle}
                selection={this.selection}
                renderLoadingRow={(
                    rowIndex: number,
                    rowDetails: ITreeRowDetails<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>
                ) => {
                    return <TableLoadingRow columns={this.columns} key={rowIndex} details={rowDetails} rowIndex={rowIndex} />;
                }}
            />
        );
    }

    private onToggle = (event: React.SyntheticEvent<HTMLElement> | undefined, treeItem: ITreeItemEx<ICodeCoverageTreeItem>) => {
        const { onToggle } = this.props;
        onToggle(treeItem);
    };
}

function getTreeColumns(): ITreeColumn<ICodeCoverageTreeItem>[] {
    return [
        {
            id: "name",
            hierarchical: true,
            name: Resources.Name,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                treeColumn: ITableColumn<ITreeItemEx<ICodeCoverageTreeItem>>,
                treeItem: ITreeItemEx<ICodeCoverageTreeItem>
            ) => {
                const codeCoverageData = treeItem.underlyingItem.data;
                const { path, isDirectory } = codeCoverageData;
                const fileName = getFileName(path);

                return (
                    <ExpandableTreeCell columnIndex={columnIndex} key={columnIndex} treeColumn={treeColumn} treeItem={treeItem}>
                        <Icon
                            className={css("margin-right-8", isDirectory && "cc-folder-icon")}
                            iconName={isDirectory ? "FabricFolderFill" : "Page"}
                            size={IconSize.medium}
                        />
                        <Tooltip text={fileName} overflowOnly={true}>
                            <div className="text-ellipsis">{fileName}</div>
                        </Tooltip>
                    </ExpandableTreeCell>
                );
            },
            minWidth: 100,
            width: new ObservableValue(-100)
        },
        {
            id: "coverage",
            name: Resources.Coverage,
            justification: ColumnJustification.Right,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                treeColumn: ITableColumn<ITreeItemEx<ICodeCoverageTreeItem>>,
                treeItem: ITreeItemEx<ICodeCoverageTreeItem>
            ) => {
                const codeCoverageData: ICodeCoverageTreeItem = treeItem.underlyingItem.data;
                const { coveragePercentage } = codeCoverageData;
                return (
                    <SimpleTableCell contentClassName={"bolt-list-cell-child"} columnIndex={columnIndex} key={columnIndex} tableColumn={treeColumn}>
                        <div className="flex-row flex-grow cc-tree-coverage">
                            <CoveredBarChart coveredPercentage={coveragePercentage} width={50} notCoveredCutoff={30} partiallyCoveredCutoff={70} />

                            <div className="padding-left-8">
                                {format(Resources.CoveragePercentageFormat, Math.abs(coveragePercentage).toFixed(2))}
                            </div>
                        </div>
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(140)
        },
        {
            id: "totalLines",
            name: Resources.TotalLines,
            justification: ColumnJustification.Right,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                treeColumn: ITableColumn<ITreeItemEx<ICodeCoverageTreeItem>>,
                treeItem: ITreeItemEx<ICodeCoverageTreeItem>
            ) => {
                const codeCoverageData: ICodeCoverageTreeItem = treeItem.underlyingItem.data;
                const { totalLines } = codeCoverageData;

                return (
                    <SimpleTableCell contentClassName={"bolt-list-cell-child"} columnIndex={columnIndex} key={columnIndex} tableColumn={treeColumn}>
                        {totalLines.toLocaleString()}
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(140)
        },
        {
            id: "notCoveredLines",
            name: Resources.NotCovered,
            justification: ColumnJustification.Right,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                treeColumn: ITableColumn<ITreeItemEx<ICodeCoverageTreeItem>>,
                treeItem: ITreeItemEx<ICodeCoverageTreeItem>
            ) => {
                const codeCoverageData: ICodeCoverageTreeItem = treeItem.underlyingItem.data;
                const { notCoveredLines } = codeCoverageData;
                return (
                    <SimpleTableCell contentClassName={"bolt-list-cell-child"} columnIndex={columnIndex} key={columnIndex} tableColumn={treeColumn}>
                        {notCoveredLines.toLocaleString()}
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(140)
        }
    ];
}
